import { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import versionHistoryService from '../../../../services/versionHistoryService';

/**
 * Hook for save operations including auto-save, manual save, and version snapshots
 */
const useSaveOperations = ({
  screens,
  setScreens,
  items,
  currentScreenIndex,
  setCurrentScreenIndex,
  deviceMode,
  canvasZoom,
  assessmentConfig,
  onSave,
  updateElementsWithScreenQuestionIdsRef,
  versionHistoryRef,
  changeSummary,
  runWithoutUndo,
  isReadOnlyRef,
}) => {
  // Save state
  const [saveStatus, setSaveStatus] = useState('saved');
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  // Refs for tracking changes
  const hasChangesSinceAutoSave = useRef(false);
  const isInitialMount = useRef(true);
  const isUpdatingScreenQuestionIds = useRef(false);
  // Blocks the 2s auto-save from firing a concurrent save_screens while a manual save
  // (and its post-save nav-rule sync) is in flight — prior race produced duplicate screens
  // and orphaned nav rules.
  const isManualSavingRef = useRef(false);

  const applyScreenQuestionMapping = useCallback(async (mapping) => {
    if (!mapping) return;
    const fn = updateElementsWithScreenQuestionIdsRef?.current;
    if (typeof fn === 'function') {
      await fn(mapping);
    }
  }, [updateElementsWithScreenQuestionIdsRef]);

  // Auto-save draft function
  const saveDraft = useCallback(async () => {
    if (isReadOnlyRef?.current) return;
    if (!autoSaveEnabled || !assessmentConfig?.assessment_id) return;
    if (isManualSavingRef.current) return;

    setSaveStatus('saving');
    try {
      const saveResult = await onSave(screens, items, { device_mode: deviceMode });
      await applyScreenQuestionMapping(saveResult?.element_screen_question_mapping);
      setSaveStatus('saved');
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save draft failed:', error);
      setSaveStatus('unsaved');
    }
  }, [autoSaveEnabled, assessmentConfig?.assessment_id, screens, items, deviceMode, onSave, applyScreenQuestionMapping]);

  // Manual save with optional version snapshot. screensOverride lets callers that
  // just computed a new screens array (e.g. template bulk-apply) persist it in the
  // same tick — the `screens` closure still holds the pre-setScreens state then.
  const handleSave = useCallback(async (skipVersionSnapshot = false, createSnapshot = false, screensOverride = null) => {
    if (isReadOnlyRef?.current) {
      toast('Read-only — unlock the assessment to save changes.', { icon: '🔒' });
      return false;
    }
    if (isManualSavingRef.current) {
      console.log('[SAVE] Manual save already in progress, ignoring duplicate trigger');
      return false;
    }
    const sourceScreens = screensOverride || screens;
    isManualSavingRef.current = true;
    setSaveStatus('saving');
    setSaving(true);
    try {
      // DEDUPLICATE before saving to prevent duplicate screens
      const deduplicatedScreens = [];
      const seenIds = new Set();
      const seenNameRanks = new Set();

      sourceScreens.forEach((screen, index) => {
        const nameRankKey = `${screen.name}_${screen.rank || index}`;
        
        if (!seenIds.has(screen.id) && !seenNameRanks.has(nameRankKey)) {
          seenIds.add(screen.id);
          seenNameRanks.add(nameRankKey);
          deduplicatedScreens.push(screen);
        } else {
          console.warn('[SAVE] Duplicate screen detected and removed:', screen.name, screen.rank);
        }
      });
      
      // Rerank screens to ensure sequential order
      const screensToSave = deduplicatedScreens.map((screen, idx) => ({
        ...screen,
        rank: idx
      }));
      
      if (screensToSave.length < sourceScreens.length) {
        console.log('[SAVE] Removed', sourceScreens.length - screensToSave.length, 'duplicate screens before saving');
        const applyDeduped = () => {
          setScreens(screensToSave);
          if (currentScreenIndex >= screensToSave.length) {
            setCurrentScreenIndex(Math.max(0, screensToSave.length - 1));
          }
        };
        if (typeof runWithoutUndo === 'function') {
          runWithoutUndo(applyDeduped);
        } else {
          applyDeduped();
        }
      }
      
      const saveResult = await onSave(screensToSave, items, { device_mode: deviceMode });
      await applyScreenQuestionMapping(saveResult?.element_screen_question_mapping);

      // Create a manual version snapshot after save
      if (createSnapshot || !skipVersionSnapshot) {
        try {
          const configuration = {
            screens: screensToSave,
            deviceMode,
            metadata: {
              element_count: screensToSave.reduce((sum, screen) => sum + (screen.elements?.length || 0), 0),
              screen_count: screensToSave.length,
            }
          };
          
          await versionHistoryService.manualSave(
            assessmentConfig.survey_id,
            configuration,
            changeSummary || 'Manual save'
          );
          
          console.log('Manual version snapshot created');
          if (createSnapshot) {
            toast.success('Version snapshot created!');
            if (versionHistoryRef?.current?.refresh) {
              versionHistoryRef.current.refresh();
            }
          }
        } catch (versionError) {
          console.error('Failed to create version snapshot:', versionError);
          if (createSnapshot) {
            toast.error('Failed to create snapshot: ' + (versionError.message || 'Unknown error'));
          }
        }
      }
      
      setSaveStatus('saved');
      setLastSaved(new Date());
      // Manual save covers all pending changes — reset so the 5-min version timer
      // doesn't fire a redundant autosave snapshot right after.
      hasChangesSinceAutoSave.current = false;
      console.log('Screens saved successfully');
      // Confirm the explicit Save action. The 'Version snapshot created!' toast
      // already covers the createSnapshot path, so don't double up there.
      if (!createSnapshot) {
        toast.success('Assessment saved');
      }
      // Resolve with the save payload rather than a bare true. A caller that follows
      // a save with a ScreenQuestion-addressed request (layout-template bulk-apply)
      // needs the element_screen_question_mapping this save just minted, because
      // save_screens deletes and recreates every ScreenQuestion row and any id held
      // in canvas state may already be dead (FECOA-2014). An empty payload still
      // resolves truthy so `=== false` stays the only failure signal.
      return saveResult || true;
    } catch (error) {
      console.error('Error saving screens:', error);
      setSaveStatus('error');
      const detail = error.response?.data?.detail || error.response?.data?.error;
      toast.error('Failed to save screens: ' + (detail || error.message || 'Unknown error'));
      return false;
    } finally {
      setSaving(false);
      isManualSavingRef.current = false;
    }
  }, [screens, setScreens, items, currentScreenIndex, setCurrentScreenIndex, deviceMode, assessmentConfig, onSave, applyScreenQuestionMapping, versionHistoryRef, changeSummary, runWithoutUndo]);

  // Persist a restored snapshot to the live assessment. The caller has already
  // loaded restoredScreens into the canvas; we push those exact screens through
  // the same save-screens path as a normal save (so screen_question IDs are
  // recreated and remapped), making the restore durable instead of in-memory only.
  const saveRestoredVersion = useCallback(async (restoredScreens, restoredDeviceMode) => {
    if (isReadOnlyRef?.current) {
      toast('Read-only — unlock the assessment to restore a version.', { icon: '🔒' });
      return;
    }
    if (!Array.isArray(restoredScreens)) return;
    isManualSavingRef.current = true;
    setSaveStatus('saving');
    setSaving(true);
    try {
      const saveResult = await onSave(restoredScreens, items, {
        device_mode: restoredDeviceMode || deviceMode,
      });
      await applyScreenQuestionMapping(saveResult?.element_screen_question_mapping);
      setSaveStatus('saved');
      setLastSaved(new Date());
      hasChangesSinceAutoSave.current = false;
      toast.success('Version restored');
    } catch (error) {
      console.error('Error saving restored version:', error);
      setSaveStatus('error');
      const detail = error.response?.data?.detail || error.response?.data?.error;
      toast.error('Failed to restore version: ' + (detail || error.message || 'Unknown error'));
    } finally {
      setSaving(false);
      isManualSavingRef.current = false;
    }
  }, [onSave, items, deviceMode, applyScreenQuestionMapping, isReadOnlyRef]);

  // Mark that screen_question_id updates are happening (to prevent auto-save loop)
  const setIsUpdatingScreenQuestionIds = useCallback((value) => {
    isUpdatingScreenQuestionIds.current = value;
  }, []);

  // Auto-save effect (2 second delay for draft saves)
  useEffect(() => {
    if (screens.length === 0 || !autoSaveEnabled) return;
    
    // Skip auto-save trigger on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Skip auto-save when updating screen_question_ids
    if (isUpdatingScreenQuestionIds.current) {
      console.log('[Auto-save] Skipping - updating screen_question_ids');
      return;
    }

    // Skip while a manual save is in flight — prevents a concurrent save_screens
    // call and keeps the save-state indicators in sync.
    if (isManualSavingRef.current) {
      return;
    }

    hasChangesSinceAutoSave.current = true;
    setSaveStatus('unsaved');
    
    const timeoutId = setTimeout(() => {
      saveDraft();
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [screens, items, deviceMode, currentScreenIndex, autoSaveEnabled, saveDraft]);

  // 5-minute auto-save version snapshot timer
  useEffect(() => {
    if (!autoSaveEnabled || !assessmentConfig?.survey_id) return;

    const autoSaveCallback = async () => {
      if (screens.length === 0) return;
      
      if (!hasChangesSinceAutoSave.current) {
        console.log('5-minute auto-save skipped - no changes detected');
        return;
      }

      try {
        const configuration = {
          screens,
          deviceMode,
          metadata: {
            element_count: screens.reduce((sum, screen) => sum + (screen.elements?.length || 0), 0),
            screen_count: screens.length,
          }
        };

        await versionHistoryService.autoSave(
          assessmentConfig.survey_id,
          configuration,
          {
            device_mode: deviceMode,
            canvas_zoom: canvasZoom,
            timestamp: new Date().toISOString()
          }
        );

        hasChangesSinceAutoSave.current = false;
        console.log('5-minute auto-save version created');
      } catch (error) {
        console.error('Auto-save version failed:', error);
      }
    };

    versionHistoryService.startAutoSave(autoSaveCallback);

    return () => {
      versionHistoryService.stopAutoSave();
    };
  }, [autoSaveEnabled, assessmentConfig?.survey_id]);

  return {
    // State
    saveStatus,
    setSaveStatus,
    lastSaved,
    setLastSaved,
    autoSaveEnabled,
    setAutoSaveEnabled,
    saving,
    setSaving,
    
    // Functions
    saveDraft,
    handleSave,
    saveRestoredVersion,
    setIsUpdatingScreenQuestionIds,
  };
};

export default useSaveOperations;
